import { useState } from "react";
import SiteLayout from "../../SiteLayout";
import { Peeker } from "../../ShadowMark";
import BookDemoButton from "../../BookDemoButton";
import "../../styles/solutions.css";

// Each persona section has a .p-grid where clicking a .feat-row activates the
// matching .fpane. In the static page a script toggled `.act`/`.cur` classes;
// here each grid owns a single `active` index in useState and derives the
// classNames from it. Initial active index is 0 (the row/pane that shipped with
// the `act`/`cur` classes on load).
function PersonaGrid({ rows, stage, panes }) {
  const [active, setActive] = useState(0);
  return (
    <div className="p-grid">
      <div className="feat">
        {rows.map((row, i) => (
          <div
            key={i}
            className={i === active ? "feat-row act" : "feat-row"}
            onClick={() => setActive(i)}
          >
            <div className="th">
              <span className="t">{row.t}</span>
              <span className={`chip ${row.chipClass}`}>{row.chip}</span>
            </div>
            <div className="d">{row.d}</div>
          </div>
        ))}
      </div>
      <div className={`fstage ${stage}`}>
        {panes.map((pane, i) => (
          <div key={i} className={i === active ? "fpane cur" : "fpane"}>
            <div className="gwin">{pane}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

const REPS_ROWS = [
  { t: "Pre-call briefs from the team graph", chip: "next", chipClass: "next", d: "Everything your team knows about the account, readable in 90 seconds." },
  { t: "Live answers, cited", chip: "now", chipClass: "now", d: "Objections classified, the team’s best response served, with the source call." },
  { t: "Discovery and storytelling support", chip: "now", chipClass: "now", d: "What’s still unasked, and the narrative frame that fits this buyer." },
  { t: "Real-time agenda tracking", chip: "now", chipClass: "now", d: "Checkpoints covered, notes captured automatically, pending items flagged before time runs out." },
  { t: "Follow-ups drafted, actions tracked to done", chip: "now", chipClass: "now", d: "Captured with owners, auto-updated via Calendar and Gmail, blockers flagged. Nothing sends without you." },
  { t: "CRM that fills itself", chip: "next", chipClass: "next", d: "Structured facts, not blob summaries. Your pipeline data finally true." },
  { t: "Relationship moments", chip: "next", chipClass: "next", d: "Promotions, funding, renewals. The right touch at the right time, draft ready." },
];

const REPS_PANES = [
  <>
    <div className="gh">brief · acme corp · 8:55 am</div>
    <div className="g"><b>Open commitment:</b> security one-pager due today</div>
    <div className="g">“Anything past $40K needs CFO sign-off” <span className="gm">· trisha · may 12</span></div>
    <div className="g">Suggested play: lead with the beachhead story</div>
  </>,
  <>
    <div className="gh">live call · objection detected</div>
    <div className="g">“How are you different from Gong?” <span className="gm">· client</span></div>
    <div className="g"><b>Shadow</b> · Gong reports after the call, for managers. We help on the live call.<br /><span className="gm">source · datastream demo · may 12</span></div>
  </>,
  <>
    <div className="gh">live call · discovery</div>
    <div className="g"><span className="gx">●</span> Tech stack still unasked · ask about their setup</div>
    <div className="g"><b>Frame:</b> the beachhead story fits this buyer <span className="gm">· won 3 similar deals</span></div>
  </>,
  <>
    <div className="gh">live call · agenda</div>
    <div className="g"><span className="gc">✓</span> Intro &nbsp;&nbsp;<span className="gc">✓</span> Discovery &nbsp;&nbsp;<span className="gx">●</span> Pricing pending</div>
    <div className="g">Notes captured automatically <span className="gm">· 3 checkpoints · 12 min left</span></div>
  </>,
  <>
    <div className="gh">after the call · follow-through</div>
    <div className="g">Follow-up email <span className="gc">drafted ✓</span> <span className="gm">· in your voice · waiting for review</span></div>
    <div className="g">Kickoff invite <span className="gm">scheduled · thu 11:00 · via calendar</span></div>
    <div className="g">Pricing doc <span className="gx">blocked</span> <span className="gm">· shadow offered a draft</span></div>
  </>,
  <>
    <div className="gh">after the call · crm sync</div>
    <div className="g">budget confirmed · <b>$48K</b> <span className="gm">→ salesforce</span></div>
    <div className="g">decision date · jun 30 <span className="gm">→ salesforce</span></div>
    <div className="g">champion · sarah lin, svp eng <span className="gm">→ salesforce</span></div>
  </>,
  <>
    <div className="gh">between meetings · signals</div>
    <div className="g">Sarah Lin <span className="gm">→</span> <b>SVP Engineering</b> <span className="gm">· signal: linkedin</span></div>
    <div className="g">Congrats draft ready · good moment for the CFO intro</div>
  </>,
];

const MANAGERS_ROWS = [
  { t: "The Monday digest in Slack", chip: "roadmap", chipClass: "future", d: "Risk, coaching moments, and workload, posted where the team already talks." },
  { t: "One coaching moment per rep per week", chip: "roadmap", chipClass: "future", d: "“Tyler hit the same pricing objection on 3 of 4 calls. Mark’s reframe converts. Clip attached.”" },
  { t: "Deal risk with the quote behind it", chip: "roadmap", chipClass: "future", d: "Not a red flag icon. The sentence the customer actually said." },
  { t: "Workload truth, not surveillance", chip: "roadmap", chipClass: "future", d: "Call load, admin debt, capacity. Work state, never rankings, and reps see everything you see." },
  { t: "Ramp on the team’s memory", chip: "roadmap", chipClass: "future", d: "New reps ask Shadow and get the team’s best play, cited. Days, not weeks." },
];

const MANAGERS_PANES = [
  <>
    <div className="gh">monday 8:01 am · slack</div>
    <div className="g"><span className="spill risk">risk</span>Acme · Committed · $48K · champion quiet 9 days</div>
    <div className="g"><span className="spill coach">coach</span>Tyler stuck on pricing · Mark’s clip attached</div>
    <div className="g"><span className="spill load">load</span>2 of 6 committed deals · no next meeting</div>
  </>,
  <>
    <div className="gh">coaching · this week</div>
    <div className="g">Tyler · stuck on pricing, 3 of 4 calls</div>
    <div className="g"><span className="gc">play</span> Mark’s reframe converts <span className="gm">· clip · 0:52</span></div>
  </>,
  <>
    <div className="gh">pipeline · committed</div>
    <div className="g"><b>Acme Corp · $48K</b> &nbsp;<span className="gx">at risk</span></div>
    <div className="g">“I need to get my CFO comfortable before we sign anything.”<br /><span className="gm">source · acme call · jun 4</span></div>
  </>,
  <>
    <div className="gh">workload · this week</div>
    <div className="g">Mark · 19 calls<div className="gmeter"><i style={{ width: "86%" }} /></div></div>
    <div className="g">Tyler · 11 calls, has capacity<div className="gmeter"><i style={{ width: "48%" }} /></div></div>
  </>,
  <>
    <div className="gh">new hire · week 2</div>
    <div className="g">“How do we handle the pricing objection on 50-seat deals?” <span className="gm">· emma</span></div>
    <div className="g"><span className="gc">answered ✓</span> Mark’s framing, cited · 0 reps interrupted</div>
  </>,
];

const CXOS_ROWS = [
  { t: "Ask anything, answered from every conversation", chip: "roadmap", chipClass: "future", d: "“Why did we lose deals last quarter?” Ranked reasons, deal counts, and the verbatim quote behind each." },
  { t: "Forecast on evidence", chip: "roadmap", chipClass: "future", d: "Commit confidence built from what customers said, not Friday CRM edits." },
  { t: "Market signal from the front line", chip: "roadmap", chipClass: "future", d: "Competitor mentions, pricing pressure, emerging objections, trending over time." },
  { t: "Voice of customer, routed", chip: "roadmap", chipClass: "future", d: "Top product themes sent to the product team with the verbatims attached." },
  { t: "Every call builds the knowledge base", chip: "next", chipClass: "next", d: "Plays, facts, and account context captured as your team talks. No forms, no wiki, stays when people leave." },
  { t: "Controls that pass review", chip: "next", chipClass: "next", d: "Consent, encryption, tenant isolation today. RBAC, redaction, audit on the roadmap, stated honestly." },
];

const CXOS_PANES = [
  <>
    <div className="gh">intelligence desk · ask anything</div>
    <div className="g"><b>?</b> Why did we lose deals last quarter?</div>
    <div className="g">1 · Salesforce depth · 17 deals <span className="gm">· quotes attached</span></div>
    <div className="g">2 · Security review stalls · 9 deals</div>
  </>,
  <>
    <div className="gh">forecast · q3 commit</div>
    <div className="g">Commit · <b>$410K</b> <span className="gm">· evidence: 9 customer quotes</span></div>
    <div className="g"><span className="gc">✓</span> “Budget approved last week” <span className="gm">· jun 4</span></div>
  </>,
  <>
    <div className="gh">market · trend</div>
    <div className="g">Competitor mentions · <b>2x</b> since their price cut<div className="gmeter"><i style={{ width: "72%" }} /></div></div>
    <div className="g">Emerging objection · “does it work offline?” <span className="gm">· 6 calls</span></div>
  </>,
  <>
    <div className="gh">voice of customer · q3</div>
    <div className="g">SSO requests · <b>14</b> this quarter <span className="gm">→ product team</span></div>
    <div className="g">“We need SSO before any rollout.” <span className="gm">· sarah · acme</span></div>
  </>,
  <>
    <div className="gh">team memory · this week</div>
    <div className="g"><span className="gc">+ play</span> mark’s pricing reframe <span className="gm">· cited 31x</span></div>
    <div className="g"><span className="gc">+ fact</span> legal needs 2 weeks for redlines <span className="gm">· cited</span></div>
    <div className="g">38 facts · 3 plays · 11 accounts <span className="gm">· nobody filled in a form</span></div>
  </>,
  <>
    <div className="gh">trust · live today</div>
    <div className="g"><span className="gc">✓</span> running natively · no bot joined</div>
    <div className="g"><span className="gc">✓</span> consent confirmed · you approve every send</div>
  </>,
];

export default function SiteSolutions() {
  return (
    <SiteLayout page="solutions" title="Solutions | Shadow for reps, managers, and CXOs" navVariant="flat" navActive="solutions">
      <header className="hero">
        <div className="wrap">
          <div style={{ fontSize: "14px", fontWeight: 600, color: "var(--red)" }}>Solutions</div>
          <h1>One Shadow.<br />Three seats it changes.</h1>
          <p className="lede" style={{ marginLeft: "auto", marginRight: "auto" }}>The same coworker and the same memory, working differently for the rep on the call, the manager running the team, and the leader answering to the board.</p>
          <div className="jump">
            <a href="#reps">For reps</a>
            <a href="#managers">For managers</a>
            <a href="#cxos">For CXOs</a>
          </div>
        </div>
      </header>

      <section className="persona" id="reps">
        <div className="wrap">
          <div className="p-head">
            <div className="who" style={{ color: "var(--red)" }}>For reps</div>
            <h2>Never walk into a call cold. Never leave one with homework.</h2>
            <p className="lede">You're juggling 30 accounts and the context lives in 200 recordings you'll never rewatch. Shadow remembers for you, backs you up live, and clears your admin before you're back at your desk.</p>
          </div>
          <div className="timeline">
            <div className="tl"><div className="k">8:55 AM</div><div className="t">The brief</div><div className="d">90 seconds: open commitments, past objections with quotes, fresh company news, the suggested play.</div></div>
            <div className="tl"><div className="k">9:23 AM</div><div className="t">The hard question</div><div className="d">"How does this work with Salesforce?" The answer is on your screen before you finish breathing in.</div></div>
            <div className="tl"><div className="k">9:31 AM</div><div className="t">Hang up, done</div><div className="d">CRM updated, follow-up drafted in your voice, your promises routed as tasks. You approve, next call.</div></div>
            <div className="tl"><div className="k">3:04 PM</div><div className="t">The nudge</div><div className="d">"Sarah got promoted to SVP. Congrats draft ready, good moment for the CFO intro. Send?"</div></div>
          </div>
          <PersonaGrid rows={REPS_ROWS} stage="indigo" panes={REPS_PANES} />
        </div>
      </section>

      <section className="persona" id="managers">
        <div className="wrap">
          <div className="p-head">
            <div className="who" style={{ color: "var(--blue)" }}>For managers</div>
            <h2>Coach with evidence. Manage by exception.</h2>
            <p className="lede">You can't rewatch ten hours of calls a week, so coaching runs on anecdotes and pipeline reviews run on whatever made it into the CRM by Friday. Shadow's Coaching Staff reads everything so you read nothing, and brings you only what needs you.</p>
          </div>
          <PersonaGrid rows={MANAGERS_ROWS} stage="blue" panes={MANAGERS_PANES} />
        </div>
      </section>

      <section className="persona" id="cxos">
        <div className="wrap">
          <div className="p-head">
            <div className="who" style={{ color: "var(--green)" }}>For CXOs</div>
            <h2>See why revenue moves, not just that it moved.</h2>
            <p className="lede">Your CRM tells you what happened. Your win/loss story runs on the last anecdote a loud rep told you. Shadow's Intelligence Desk reads every conversation all quarter, so every claim has a citation behind it.</p>
          </div>
          <PersonaGrid rows={CXOS_ROWS} stage="green" panes={CXOS_PANES} />
        </div>
      </section>

      <section className="cta peek-host">
        <Peeker className="peeker" style={{ right: "10%", top: "-41px", width: "104px", height: "44px" }} />
        <div className="wrap">
          <h2>Start with one rep.<br />Let it compound.</h2>
          <p>Book a demo and see Shadow live on your next call.</p>
          <BookDemoButton className="btn" context="Solutions" />
        </div>
      </section>
    </SiteLayout>
  );
}
